import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Presentation } from "lucide-react";

export default function Presentacion() {
  const nav = useNavigate();
  const [sections, setSections] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    fetch("/guia_slides/slides.json").then((r) => r.json()).then(setSections).catch(() => {});
  }, []);

  const flat = sections.flatMap((sec) =>
    sec.slides.map((sl) => ({ ...sl, section: sec }))
  );
  const total = flat.length;
  const cur = flat[idx];

  const go = useCallback((d) => {
    setIdx((i) => Math.min(Math.max(i + d, 0), Math.max(total - 1, 0)));
  }, [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") go(1);
      else if (e.key === "ArrowLeft" || e.key === "PageUp") go(-1);
      else if (e.key === "Escape") nav("/dashboard");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, nav]);

  if (!cur) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#001E66] text-[#F8F2E6]" data-testid="presentation-loading">
        <Presentation className="mr-3" /> Cargando presentación…
      </div>
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-[#001E66] text-[#F8F2E6] flex flex-col" data-testid="presentation-mode">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/brand/lala_elite_badge_lg.png" alt="Lala Elite" className="h-8 w-8 object-contain" />
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-[#C79A2A]" data-testid="presentation-section">
              Sección {cur.section.num} · {cur.section.title}
            </div>
            <div className="font-semibold truncate" data-testid="presentation-title">{cur.title}</div>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <span className="text-sm text-white/60" data-testid="presentation-counter">{idx + 1} / {total}</span>
          <button onClick={() => nav("/dashboard")} data-testid="presentation-exit"
            className="p-2 rounded-full hover:bg-white/10 transition-colors" title="Salir (Esc)">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex min-h-0">
        <button onClick={() => go(-1)} disabled={idx === 0} data-testid="presentation-prev"
          className="px-2 md:px-4 flex items-center text-white/40 hover:text-white disabled:opacity-20 transition-colors">
          <ChevronLeft size={34} />
        </button>
        <div className="flex-1 flex flex-col lg:flex-row gap-5 items-center justify-center p-4 min-h-0">
          <div className="flex-1 h-full flex items-center justify-center min-w-0">
            <img src={cur.img} alt={cur.title} data-testid="presentation-image"
              className="max-h-full max-w-full object-contain rounded-lg border-2 border-[#0033AD] shadow-2xl bg-white" />
          </div>
          <div className="w-full lg:w-[340px] shrink-0 bg-white/[0.06] rounded-xl p-5 backdrop-blur" data-testid="presentation-panel">
            <div className="text-[#C79A2A] font-bold text-sm mb-1">¿Qué estás viendo?</div>
            <p className="text-sm italic text-white/70 mb-3">{cur.subtitle}</p>
            <ul className="space-y-2">
              {cur.bullets.map((b, i) => (
                <li key={i} className="text-[13px] leading-snug flex gap-2">
                  <span className="text-[#E4002B]">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <button onClick={() => go(1)} disabled={idx === total - 1} data-testid="presentation-next"
          className="px-2 md:px-4 flex items-center text-white/40 hover:text-white disabled:opacity-20 transition-colors">
          <ChevronRight size={34} />
        </button>
      </div>

      {/* Progress */}
      <div className="h-1.5 bg-white/10">
        <div className="h-full bg-[#E4002B] transition-all duration-300" style={{ width: `${((idx + 1) / total) * 100}%` }} />
      </div>
      <div className="px-6 py-2 flex flex-wrap gap-1.5 items-center justify-center">
        {sections.map((sec) => {
          const first = flat.findIndex((f) => f.section.num === sec.num);
          const active = cur.section.num === sec.num;
          return (
            <button key={sec.num} onClick={() => setIdx(first)} data-testid={`presentation-jump-${sec.num}`}
              className={`px-2.5 py-1 rounded-full text-[11px] transition-colors ${active ? "bg-[#C79A2A] text-[#001E66] font-bold" : "bg-white/10 text-white/60 hover:bg-white/20"}`}>
              {sec.num}. {sec.title.split(" — ")[0].split(" - ")[0]}
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  );
}
